import { type FC, memo, type ReactNode } from 'react'

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { resolveFileIcon, truncateFilename } from './chat-file-icon.helper'
import { formatFileSize, getFileIconName } from './chat-input-attachment.interface'

export interface ChatFileChipProps {
  filename: string

  mimeType: string

  sizeBytes: number

  semanticClass: string

  children?: ReactNode

  className?: string

  onClick?: () => void
}

const chipBaseClass =
  'inline-flex items-center gap-1.5 rounded-md border bg-muted/50 px-2 py-1 text-xs'

interface ChipInnerProps {
  iconName: string
  truncatedName: string
  fileSize: string
  children?: ReactNode
}

const ChipInner: FC<ChipInnerProps> = ({ iconName, truncatedName, fileSize, children }) => {
  const IconComponent = resolveFileIcon(iconName)
  return (
    <>
      <IconComponent className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate font-medium">{truncatedName}</span>
      <span className="shrink-0 text-muted-foreground">({fileSize})</span>
      {children}
    </>
  )
}

const ChatFileChipBase: FC<ChatFileChipProps> = ({
  filename,
  mimeType,
  sizeBytes,
  semanticClass,
  children,
  className,
  onClick,
}): ReactNode => {
  const iconName = getFileIconName(mimeType)
  const truncatedName = truncateFilename(filename)
  const isNameTruncated = truncatedName !== filename
  const fileSize = formatFileSize(sizeBytes)
  const innerProps = { iconName, truncatedName, fileSize, children }

  const content = onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        semanticClass,
        chipBaseClass,
        'cursor-pointer transition-colors hover:bg-muted',
        className
      )}
    >
      <ChipInner {...innerProps} />
    </button>
  ) : (
    <div className={cn(semanticClass, chipBaseClass, className)}>
      <ChipInner {...innerProps} />
    </div>
  )

  if (isNameTruncated) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top">{filename}</TooltipContent>
      </Tooltip>
    )
  }

  return content
}

export const ChatFileChip = memo(ChatFileChipBase)

ChatFileChip.displayName = 'ChatFileChip'
